import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { Design } from '../../../../../viewer/elements/design';
import { Dxf } from '../../../../../viewer/elements/dxf';
import { Geometry } from '../../../../../viewer/elements/geometry';
import { Glb } from '../../../../../viewer/elements/glb';
import { TerrainModel } from '../../../../../viewer/elements/terrain-model';
import { Icon } from '../../../icon/icon';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Stack } from '../../../stack/stack';
import { numberOrDefault } from '../../asset-menu/utils/formatting';
import { useAssetChangedEvent } from '../../hooks/use-asset-changed-event';
import {
  countGLBTriangles,
  countTriangles,
  getMeshDataDxf,
  translateVolumeCalculationType,
  VolumeCalculationInfo,
  VolumeCalculationOption,
  VolumeCalculationType,
} from './volume-calculation-helper';

interface Props {
  selectedOption: VolumeCalculationOption | undefined;
  selectedOptionInfo: VolumeCalculationInfo | undefined;
  onUpdate: (value: VolumeCalculationInfo | undefined) => void;
}

export const VolumeInfoBlock = ({ selectedOption, selectedOptionInfo, onUpdate }: Props) => {
  const { t } = useTranslation();

  const [type, setType] = useState<VolumeCalculationType | undefined>(
    selectedOption?.volumeCalculationDataType,
  );

  const { assetChangedEvent } = useAssetChangedEvent(selectedOption?.asset);

  useEffect(() => {
    if (!selectedOption) {
      onUpdate({ triangles: 0 });
      setType(undefined);
      return;
    }

    setType(selectedOption.volumeCalculationDataType);

    if (selectedOption.asset instanceof Geometry && selectedOption.asset.areaMesh) {
      onUpdate({
        meshes: [selectedOption.asset.areaMesh],
        triangles: countTriangles([selectedOption.asset.areaMesh]),
      });
    } else if (selectedOption.asset instanceof Glb && selectedOption.asset.glbData) {
      onUpdate({
        meshes: countGLBTriangles(selectedOption.asset.glbData).meshes,
        triangles: countGLBTriangles(selectedOption.asset.glbData).countTriangles,
      });
    } else if (selectedOption.asset instanceof Dxf) {
      onUpdate({
        meshes: getMeshDataDxf(selectedOption.asset).meshes,
        triangles: selectedOption.triangleCount,
      });
    } else if (selectedOption.asset instanceof TerrainModel && selectedOption.asset.glbData) {
      onUpdate({
        meshes: [selectedOption.asset.glbData],
        triangles: countTriangles([selectedOption.asset.glbData]),
      });
    } else if (selectedOption.asset instanceof Design) {
      onUpdate({
        meshes:
          selectedOption.asset.designNode.faceNodeCount > 0
            ? [selectedOption.asset.designNode.data.faces]
            : [],
        triangles: selectedOption?.triangleCount,
      });
    } else if (selectedOption.pointCloudId) {
      onUpdate({
        triangles: 0,
      });
    } else {
      onUpdate(undefined);
    }
  }, [assetChangedEvent, onUpdate, selectedOption]);

  const renderTriangleCount = () => {
    if (!isDefined(selectedOptionInfo?.triangles)) {
      return <Icon icon={['fas', 'spinner']} spin={true} />;
    }
    if (selectedOptionInfo?.triangles === 0) {
      return '-';
    }
    return numberOrDefault(selectedOptionInfo.triangles, undefined, 0);
  };

  return (
    <Component direction={'row'} spacing={0.5}>
      <LabelledContainer text={t('selectedObject.type', { ns: 'skyviewVolumeCalculation' })}>
        <span>{isDefined(type) ? translateVolumeCalculationType(type) : '-'}</span>
      </LabelledContainer>
      <LabelledContainer
        noWrap={true}
        text={t('selectedObject.numberOfTriangles', { ns: 'skyviewVolumeCalculation' })}
      >
        <span>{renderTriangleCount()}</span>
      </LabelledContainer>
    </Component>
  );
};

const Component = styled(Stack)`
  > * {
    flex: 1;
  }
`;
